// src/components/ViewFeedbackDialog.tsx
import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  Stack,
  IconButton,
  Avatar,
  Grid,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BugReportIcon from '@mui/icons-material/BugReport';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ReplyIcon from '@mui/icons-material/Reply';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ImageIcon from '@mui/icons-material/Image';
import { useTranslation } from 'react-i18next';
import { Feedback, ReportType } from '../types/feedback';

const REPORT_TYPE_CONFIG: Record<ReportType, { icon: React.ReactElement; color: string; bgColor: string; label: string }> = {
  bug: { icon: <BugReportIcon sx={{ fontSize: 20 }} />, color: '#dc2626', bgColor: '#fef2f2', label: 'Bug' },
  feature: { icon: <LightbulbIcon sx={{ fontSize: 20 }} />, color: '#d97706', bgColor: '#fef3c7', label: 'Fonctionnalité' },
  complaint: { icon: <ReportProblemIcon sx={{ fontSize: 20 }} />, color: '#db2777', bgColor: '#fce7f3', label: 'Plainte' },
  general: { icon: <InfoIcon sx={{ fontSize: 20 }} />, color: '#2563eb', bgColor: '#dbeafe', label: 'Général' },
  other: { icon: <HelpOutlineIcon sx={{ fontSize: 20 }} />, color: '#64748b', bgColor: '#f1f5f9', label: 'Autre' },
};

interface ViewFeedbackDialogProps {
  open: boolean;
  onClose: () => void;
  feedback: Feedback;
  onRespond: (feedback: Feedback) => void;
  onDelete: (feedback: Feedback) => void;
}

const ViewFeedbackDialog: React.FC<ViewFeedbackDialogProps> = ({
  open,
  onClose,
  feedback,
  onRespond,
  onDelete,
}) => {
  const { t } = useTranslation();
  const typeConfig = REPORT_TYPE_CONFIG[feedback.report_type] || REPORT_TYPE_CONFIG.other;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ p: 0 }}>
        {/* Header with gradient */}
        <Box sx={{ p: 3, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box>
              {/* Type and Status Badges */}
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.5, py: 0.5, bgcolor: typeConfig.bgColor, borderRadius: 0.5 }}>
                  {React.cloneElement(typeConfig.icon, { sx: { fontSize: 18, color: typeConfig.color } })}
                  <Typography variant="caption" fontWeight={600} sx={{ color: typeConfig.color, textTransform: 'uppercase' }}>
                    {t(`admin.feedbacks.reportTypes.${feedback.report_type}`)}
                  </Typography>
                </Box>
                {feedback.is_resolved ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#dcfce7', borderRadius: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                    <Typography variant="caption" fontWeight={600} color="#16a34a">
                      {t('admin.feedbacks.resolved')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.5, bgcolor: '#fef3c7', borderRadius: 0.5 }}>
                    <ScheduleIcon sx={{ fontSize: 16, color: '#d97706' }} />
                    <Typography variant="caption" fontWeight={600} color="#d97706">
                      {t('admin.feedbacks.pending')}
                    </Typography>
                  </Box>
                )}
              </Stack>
              <Typography variant="h5" fontWeight={700} color="#1e293b">
                {feedback.title}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small" sx={{ bgcolor: 'white', '&:hover': { bgcolor: '#f1f5f9' } }}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Description */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
            {t('admin.feedbacks.description')}
          </Typography>
          <Typography variant="body1" color="#1e293b" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
            {feedback.description}
          </Typography>
        </Box>

        {/* Screenshot */}
        {feedback.screenshot_url && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <ImageIcon sx={{ fontSize: 16 }} />
                <span>{t('feedback.screenshot')}</span>
              </Stack>
            </Typography>
            <Box
              component="a"
              href={feedback.screenshot_url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'block',
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: '#2563eb',
                  boxShadow: '0 4px 12px rgba(37, 99, 235, 0.15)',
                },
              }}
            >
              <Box
                component="img"
                src={feedback.screenshot_url}
                alt={t('feedback.screenshot')}
                sx={{
                  width: '100%',
                  maxHeight: 400,
                  objectFit: 'contain',
                  bgcolor: '#f8fafc',
                  display: 'block',
                }}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {t('feedback.clickToEnlarge')}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* User & Dates Info */}
        <Grid container spacing={4}>
          {/* User Information */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
              {t('admin.feedbacks.userInfo')}
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: '#2563eb' }}>
                  {feedback.user_name?.charAt(0).toUpperCase() || '?'}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight={600} color="#1e293b">
                    {feedback.user_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {feedback.user_email}
                  </Typography>
                </Box>
              </Stack>
              {feedback.contact_email && feedback.contact_email !== feedback.user_email && (
                <Box sx={{ pl: 7 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon sx={{ fontSize: 16, color: '#64748b' }} />
                    <Typography variant="body2" color="text.secondary">
                      Contact: {feedback.contact_email}
                    </Typography>
                  </Stack>
                </Box>
              )}
            </Stack>
          </Grid>

          {/* Dates */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" fontWeight={600} color="#64748b" sx={{ mb: 2, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
              {t('admin.feedbacks.dates')}
            </Typography>
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1, bgcolor: '#f1f5f9', borderRadius: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 16, color: '#64748b' }} />
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">{t('admin.feedbacks.createdAt')}</Typography>
                  <Typography variant="body2" fontWeight={500} color="#1e293b">{formatDate(feedback.created_at)}</Typography>
                </Box>
              </Box>
              {feedback.resolved_at && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ p: 1, bgcolor: '#dcfce7', borderRadius: 0.5 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('admin.feedbacks.resolvedAt')}</Typography>
                    <Typography variant="body2" fontWeight={500} color="#16a34a">{formatDate(feedback.resolved_at)}</Typography>
                  </Box>
                </Box>
              )}
            </Stack>
          </Grid>
        </Grid>

        {/* Admin Response */}
        {feedback.admin_response && (
          <>
            <Divider sx={{ my: 3 }} />
            <Box sx={{ p: 3, bgcolor: '#f0fdf4', borderRadius: 1, borderLeft: '4px solid #16a34a' }}>
              <Typography variant="subtitle2" fontWeight={600} color="#16a34a" sx={{ mb: 1.5, textTransform: 'uppercase', fontSize: 11, letterSpacing: 0.5 }}>
                {t('admin.feedbacks.response')}
              </Typography>
              <Typography variant="body1" color="#1e293b" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                {feedback.admin_response}
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, borderTop: '1px solid #e2e8f0', gap: 1 }}>
        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => onDelete(feedback)}
          sx={{ borderRadius: 1 }}
        >
          {t('admin.feedbacks.delete')}
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        {!feedback.is_resolved && (
          <Button
            variant="contained"
            startIcon={<ReplyIcon />}
            onClick={() => onRespond(feedback)}
            sx={{ borderRadius: 1, bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
          >
            {t('admin.feedbacks.respond')}
          </Button>
        )}
        <Button variant="outlined" onClick={onClose} sx={{ borderRadius: 1 }}>
          {t('common.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewFeedbackDialog;
